/**
 * Dados de exemplo para os testes de ponta a ponta.
 *
 * Nasceu do modo demonstração ("Experimentar sem conta"), que saiu do produto:
 * hoje a conta se cria com senha ou Google. O que sobrou é fixture -- os
 * testes precisam de uma agenda com clientes, serviços e estoque para abrir
 * as telas sem subir a API.
 *
 * NÃO é alcançável pela interface. Quem chama é o atalho de teste exposto em
 * apps/web, atrás de VITE_E2E=1, que nunca entra num build de produção.
 */
import type { LocalDb } from '../db/local-db';
import { SUGGESTED_SERVICES, SERVICE_COLORS } from '../domain/defaults';
import { uuid } from '../domain/ids';
import { DAY, MINUTE, addDays, deviceTimeZone, todayIn, toIso, zonedToUtc } from '../domain/time';
import type { AppointmentStatus, PaymentMethod, StockUnit } from '../schemas/entities';

/** Produtos do estoque de exemplo: [nome, medida, embalagem, pagou, vende por, avisar com, embalagens que entraram, dias atrás]. */
const PRODUCTS: Array<[string, StockUnit, number, number, number | null, number | null, number, number]> = [
  ['Base fortalecedora', 'un', 1, 1200, 2400, 1, 1, 30],
  ['Gel construtor', 'g', 30, 8900, null, 30, 1, 45],
  ['Creme para mãos', 'un', 1, 2200, 4500, 3, 4, 30],
  ['Esmalte Risqué Nude', 'un', 1, 850, 1500, 2, 8, 30],
  ['Esmalte Risqué Vermelho', 'un', 1, 850, 1500, 2, 8, 30],
  ['Lixa 100/180', 'un', 1, 120, null, 10, 50, 45],
  ['Óleo de cutícula', 'un', 1, 900, 1900, 2, 7, 30],
  ['Removedor de esmalte', 'ml', 500, 1800, null, 200, 2, 45],
  ['Top coat', 'un', 1, 1400, null, null, 4, 30],
];

/** O que cada serviço gasta: [serviço, produto, quantidade na medida]. */
const SUPPLIES: Array<[string, string, number]> = [
  ['Manutenção de gel', 'Gel construtor', 4],
  ['Manutenção de gel', 'Lixa 100/180', 1],
  ['Aplicação de gel', 'Gel construtor', 6],
  ['Aplicação de gel', 'Lixa 100/180', 2],
  ['Alongamento', 'Gel construtor', 6],
  ['Alongamento', 'Lixa 100/180', 2],
  ['Pé e mão', 'Lixa 100/180', 1],
  ['Pé e mão', 'Removedor de esmalte', 25],
  ['Esmaltação', 'Removedor de esmalte', 25],
];

const CLIENT_NAMES: Array<[string, string]> = [
  ['Fernanda Lima', '92991234567'],
  ['Maria Souza', '92992345678'],
  ['Joana Pereira', '92993456789'],
  ['Patrícia Alves', '92994567890'],
  ['Camila Rocha', '92995678901'],
  ['Luciana Martins', '92996789012'],
  ['Beatriz Costa', '92997890123'],
  ['Renata Gomes', '92998901234'],
  ['Aline Ferreira', '92999012345'],
  ['Simone Barbosa', '92990123456'],
];

export async function semearExemplo(
  db: LocalDb,
  now: number,
  setSession: (professionalId: string) => Promise<void>,
): Promise<string> {
  const professionalId = uuid();
  await setSession(professionalId);
  const tz = deviceTimeZone();
  const nowIso = toIso(now);
  const stamp = { created_at: nowIso, updated_at: null, client_updated_at: nowIso, version: 0, deleted_at: null };

  await db.batch(async () => {
    await db.putLocal('professional', {
      ...stamp,
      id: professionalId,
      name: 'Ana Ribeiro',
      business_name: 'Studio Ana',
      slug: 'studio-ana',
      email: null,
      phone: '5592991112222',
      timezone: tz,
      address: 'Rua das Flores, 120, sala 3',
      serves_at_home: true,
      subscription_status: 'trialing',
      trial_ends_at: toIso(now + 14 * DAY),
      onboarded_at: nowIso,
    });

    const services = SUGGESTED_SERVICES.map((s, i) => ({
      ...stamp,
      id: uuid(),
      name: s.name,
      duration_min: s.duration_min,
      price_cents: s.price_cents,
      return_days: s.return_days,
      color: SERVICE_COLORS[i % SERVICE_COLORS.length] ?? null,
      sort_order: i,
      visible_on_link: true,
      active: true,
    }));
    for (const s of services) await db.putLocal('service', s);
    const byName = (n: string) => services.find((s) => s.name === n)!;

    for (const weekday of [1, 2, 3, 4, 5, 6]) {
      await db.putLocal('availability_rule', { ...stamp, id: uuid(), weekday, start: '09:00', end: weekday === 6 ? '14:00' : '18:00' });
    }

    const clients = CLIENT_NAMES.map(([name, phone]) => ({
      ...stamp,
      id: uuid(),
      name,
      whatsapp: `55${phone}`,
      access_code: null,
      notes: name.startsWith('Maria') ? 'Prefere esmalte nude. Alergia a acetona forte.' : null,
    }));
    for (const c of clients) await db.putLocal('client', c);

    const today = todayIn(tz, now);
    const at = (dayOffset: number, time: string) => toIso(zonedToUtc(addDays(today, dayOffset), time, tz));

    // Estoque: produtos com a entrada de cada um e o que cada serviço gasta.
    const products = PRODUCTS.map(([name, unit, pack_size, cost_cents, price_cents, min_qty]) => ({
      ...stamp,
      id: uuid(),
      name,
      unit,
      pack_size,
      cost_cents,
      price_cents,
      min_qty,
      active: true,
    }));
    for (const [i, p] of products.entries()) {
      await db.putLocal('product', p);
      const [, , , cost, , , packs, daysAgo] = PRODUCTS[i]!;
      await db.putLocal('stock_movement', {
        ...stamp,
        id: uuid(),
        product_id: p.id,
        kind: 'in',
        qty: packs * p.pack_size,
        cost_cents: packs * cost,
        price_cents: 0,
        appointment_id: null,
        sale_id: null,
        note: null,
        occurred_at: at(-daysAgo, '08:00'),
      });
    }
    const productByName = (n: string) => products.find((p) => p.name === n)!;
    const supplies = SUPPLIES.map(([service, product, qty]) => ({ ...stamp, id: uuid(), service_id: byName(service).id, product_id: productByName(product).id, qty }));
    for (const s of supplies) await db.putLocal('service_supply', s);

    const add = async (
      clientIdx: number,
      serviceName: string,
      dayOffset: number,
      time: string,
      status: AppointmentStatus,
      method: PaymentMethod | null = null,
      extra: Record<string, unknown> = {},
    ) => {
      const service = byName(serviceName);
      const start = at(dayOffset, time);
      const end = toIso(Date.parse(start) + service.duration_min * MINUTE);
      const id = uuid();
      await db.putLocal('appointment', {
        ...stamp,
        id,
        client_id: clients[clientIdx]!.id,
        service_id: service.id,
        start_utc: start,
        end_utc: end,
        status,
        source: 'professional',
        price_cents: service.price_cents,
        completed_at: status === 'completed' ? end : null,
        ...extra,
      });
      if (status === 'completed') {
        for (const supply of supplies.filter((x) => x.service_id === service.id)) {
          await db.putLocal('stock_movement', {
            ...stamp,
            id: uuid(),
            product_id: supply.product_id,
            kind: 'use',
            qty: -supply.qty,
            cost_cents: 0,
            price_cents: 0,
            appointment_id: id,
            sale_id: null,
            note: null,
            occurred_at: end,
          });
        }
      }
      if (status === 'completed' && method) {
        await db.putLocal('payment', {
          ...stamp,
          id: uuid(),
          appointment_id: id,
          client_id: clients[clientIdx]!.id,
          amount_cents: service.price_cents,
          method,
          paid_at: end,
        });
      }
      return id;
    };

    // Hoje
    await add(0, 'Manutenção de gel', 0, '09:00', 'confirmed');
    await add(1, 'Pé e mão', 0, '10:30', 'scheduled');
    await add(2, 'Esmaltação', 0, '14:00', 'scheduled', null, { source: 'link' });
    await add(3, 'Alongamento', 0, '15:00', 'scheduled');
    // Amanhã
    await add(4, 'Manutenção de gel', 1, '09:30', 'scheduled');
    await add(5, 'Limpeza de pele', 1, '11:00', 'scheduled');
    await add(6, 'Esmaltação', 1, '15:30', 'pending', null, { source: 'link' });
    // Passado (caixa, histórico e Quem sumiu)
    const methods: PaymentMethod[] = ['pix', 'cash', 'card', 'pix'];
    await add(0, 'Manutenção de gel', -21, '09:00', 'completed', 'pix');
    await add(1, 'Pé e mão', -14, '10:00', 'completed', 'cash');
    await add(2, 'Esmaltação', -7, '11:00', 'completed', 'pix');
    await add(3, 'Aplicação de gel', -24, '14:00', 'completed', 'card');
    await add(7, 'Manutenção de gel', -35, '10:00', 'completed', 'pix');
    await add(8, 'Pé e mão', -40, '16:00', 'completed', 'cash');
    await add(9, 'Esmaltação', -10, '09:00', 'no_show');
    await add(9, 'Esmaltação', -28, '09:00', 'completed', null);
    for (let i = 1; i <= 4; i++) await add(i % 7, 'Esmaltação', -i, '17:00', 'completed', methods[i % methods.length]!);

    // Vendas de produto (dias passados: o caixa de hoje começa zerado)
    const sell = async (clientIdx: number | null, items: Array<[string, number]>, dayOffset: number, method: PaymentMethod) => {
      const soldAt = at(dayOffset, '12:00');
      const saleId = uuid();
      const clientId = clientIdx === null ? null : clients[clientIdx]!.id;
      let total = 0;
      for (const [name, packs] of items) {
        const p = productByName(name);
        const price = packs * (p.price_cents ?? 0);
        total += price;
        await db.putLocal('stock_movement', {
          ...stamp,
          id: uuid(),
          product_id: p.id,
          kind: 'sale',
          qty: -packs * p.pack_size,
          cost_cents: 0,
          price_cents: price,
          appointment_id: null,
          sale_id: saleId,
          note: null,
          occurred_at: soldAt,
        });
      }
      await db.putLocal('sale', { ...stamp, id: saleId, client_id: clientId, appointment_id: null, total_cents: total, sold_at: soldAt });
      await db.putLocal('payment', { ...stamp, id: uuid(), appointment_id: null, package_id: null, sale_id: saleId, client_id: clientId, amount_cents: total, method, paid_at: soldAt });
    };
    await sell(4, [['Creme para mãos', 1]], -3, 'pix');
    await sell(null, [['Óleo de cutícula', 1]], -5, 'cash');
    await sell(2, [['Esmalte Risqué Nude', 2]], -8, 'card');
    await sell(7, [['Creme para mãos', 1], ['Base fortalecedora', 1]], -12, 'pix');

    // Pacote de limpeza de pele (esteticista)
    await db.putLocal('package', {
      ...stamp,
      id: uuid(),
      client_id: clients[5]!.id,
      service_id: byName('Limpeza de pele').id,
      sessions_total: 10,
      sessions_used_initial: 3,
      price_cents: 120000,
      purchased_at: toIso(now - 30 * DAY),
      expires_at: null,
    });
  });

  return professionalId;
}
